import React, { useEffect, useState } from 'react'

export default function Kontakt() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Mandatory honeypot field
  })
  const [status, setStatus] = useState({ type: null, message: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    document.title = 'Kontakt - Borgs Køreskole I/S'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Kontakt Borgs Køreskole I/S i Kalundborg. Ring på 21 47 01 87 / 23 20 04 25 eller send en besked for tilmelding til næste hold for kørekort Kat. B.'
      )
    }
  }, [])

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus({ type: null, message: '' })

    // Validation check
    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim() || !formData.message.trim()) {
      setStatus({ type: 'error', message: 'Udfyld venligst alle påkrævede felter.' })
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch('https://api.dev.auxo.dkwebform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          website_url: formData.website_url // Honeypot
        })
      })

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Mange tak for din henvendelse! Vi vender tilbage hurtigst muligt.'
        })
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: '',
          website_url: ''
        })
      } else {
        setStatus({
          type: 'error',
          message: 'Der opstod en fejl ved afsendelse. Ring venligst direkte på 21 47 01 87 eller 23 20 04 25.'
        })
      }
    } catch (error) {
      console.error('Webform submission error:', error)
      setStatus({
        type: 'error',
        message: 'Netværksfejl. Ring venligst direkte til os på 21 47 01 87 eller 23 20 04 25.'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-slate-50 min-h-screen py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-left max-w-3xl mb-16">
          <h1 className="font-header text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
            Kontakt Borgs Køreskole I/S
          </h1>
          <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
            Har du spørgsmål til holdstart, køretimer eller pris? Ring til os direkte eller udfyld formularen herunder.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Contact Details Card */}
          <div className="lg:col-span-5 bg-emerald-900 text-white p-8 sm:p-10 shadow-xl space-y-8">
            <div>
              <span className="text-white text-xs font-bold uppercase tracking-widest block mb-1">
                Direkte Kontakt
              </span>
              <h2 className="font-header text-2xl sm:text-3xl font-bold text-white">
                Borgs Køreskole I/S
              </h2>
              <p className="text-white text-sm mt-2 leading-relaxed">
                Din lokale køreskole med nærværende køreundervisning i Kalundborg.
              </p>
            </div>

            <div className="divide-y divide-emerald-800/80 text-sm">
              <div className="flex items-start gap-4 pb-5">
                <i className="fa-solid fa-phone text-white text-xl flex-shrink-0 mt-1" aria-hidden="true"></i>
                <div>
                  <span className="text-xs text-white uppercase font-bold block">Telefon</span>
                  <div className="flex flex-col sm:flex-row sm:gap-3 gap-1 mt-1">
                    <a href="tel:21470187" className="text-white text-lg font-bold hover:underline transition-colors whitespace-nowrap">
                      21 47 01 87
                    </a>
                    <span className="hidden sm:inline text-white/60">/</span>
                    <a href="tel:23200425" className="text-white text-lg font-bold hover:underline transition-colors whitespace-nowrap">
                      23 20 04 25
                    </a>
                  </div>
                  <span className="text-xs text-white block mt-0.5">Ring eller send en SMS</span>
                </div>
              </div>

              <div className="flex items-start gap-4 py-5">
                <i className="fa-solid fa-envelope text-white text-xl flex-shrink-0 mt-1" aria-hidden="true"></i>
                <div>
                  <span className="text-xs text-white uppercase font-bold block">Email</span>
                  <a href="mailto:info@borgskoereskole.dk" className="text-white text-base font-bold hover:underline transition-colors whitespace-nowrap">
                    info@borgskoereskole.dk
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 py-5">
                <i className="fa-solid fa-location-dot text-white text-xl flex-shrink-0 mt-1" aria-hidden="true"></i>
                <div>
                  <span className="text-xs text-white uppercase font-bold block">Adresse</span>
                  <address className="not-italic text-white text-base font-semibold">
                    Elmegade 19<br />4400 Kalundborg
                  </address>
                </div>
              </div>

              <div className="flex items-start gap-4 pt-5">
                <i className="fa-solid fa-id-card text-white text-xl flex-shrink-0 mt-1" aria-hidden="true"></i>
                <div>
                  <span className="text-xs text-white uppercase font-bold block">CVR-nummer</span>
                  <span className="text-white font-semibold">43296000</span>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-emerald-800 text-xs text-white">
              <i className="fa-solid fa-clock text-white mr-2" aria-hidden="true"></i>
              Telefontid: Alle hverdage kl. 08.00 – 18.00
            </div>
          </div>

          {/* Form Card */}
          <div className="lg:col-span-7 bg-white p-8 sm:p-10 border border-slate-200 shadow-xl">
            <h2 className="font-header text-2xl font-bold text-slate-900 mb-6">
              Send os en besked / Tilmelding
            </h2>

            {status.message && (
              <div
                className={`p-4 text-sm font-semibold mb-6 ${
                  status.type === 'success'
                    ? 'bg-emerald-50 text-emerald-900 border border-emerald-200'
                    : 'bg-red-50 text-red-900 border border-red-200'
                }`}
              >
                {status.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              
              {/* HONEYPOT FIELD (MANDATORY per rule 11) */}
              <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
                <label htmlFor="website_url">Website URL (leave empty)</label>
                <input
                  type="text"
                  id="website_url"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <div>
                <label htmlFor="name" className="block text-xs font-bold uppercase text-slate-700 mb-1.5">
                  Fulde Navn <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="F.eks. Mette Frederiksen"
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20 outline-none text-slate-900 text-sm transition-all"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="phone" className="block text-xs font-bold uppercase text-slate-700 mb-1.5">
                    Telefonnummer <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="F.eks. 21470187"
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20 outline-none text-slate-900 text-sm transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-bold uppercase text-slate-700 mb-1.5">
                    Emailadresse <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="din.email@eksempel.dk"
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20 outline-none text-slate-900 text-sm transition-all"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-xs font-bold uppercase text-slate-700 mb-1.5">
                  Beskrivelse / Besked <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Skriv din besked eller angiv hvilket teorihold du ønsker at tilmelde dig..."
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20 outline-none text-slate-900 text-sm transition-all resize-y"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 px-6 rounded-full bg-[#8cc73d] hover:bg-[#7eb832] disabled:bg-slate-400 text-slate-950 font-bold text-base transition-all flex items-center justify-center gap-2 active:scale-[0.99] cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <i className="fa-solid fa-spinner animate-spin" aria-hidden="true"></i>
                    <span>Sender besked...</span>
                  </>
                ) : (
                  <>
                    <span>Send henvendelse</span>
                    <i className="fa-solid fa-paper-plane" aria-hidden="true"></i>
                  </>
                )}
              </button>
            </form>
          </div>

        </div>
      </div>
    </div>
  )
}
